import { useEffect, useState } from 'react'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'
import { updateMeProfile } from '../../api/me'
import { listRelationships, blockHandle, deleteRelationship, type Relationship } from '../../api/relationships'
import BlockedAccountRow from '../../components/settings/BlockedAccountRow'
import { getContentPreferences, updateContentPreferences } from '../../api/contentPreferences'

export default function PrivacySettings() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const [isPrivate, setIsPrivate] = useState(false)
  const [tipsEnabled, setTipsEnabled] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [blocked, setBlocked] = useState<Relationship[] | null>(null)
  const [loadingBlocked, setLoadingBlocked] = useState(false)
  const [handleInput, setHandleInput] = useState('')
  const [unblockId, setUnblockId] = useState<number | null>(null)
  const [blocking, setBlocking] = useState(false)
  const [allowSensitiveNudity, setAllowSensitiveNudity] = useState(false)
  const [allowSensitiveViolence, setAllowSensitiveViolence] = useState(false)
  const [blurSensitivePreviews, setBlurSensitivePreviews] = useState(true)
  const [ageConfirmed18, setAgeConfirmed18] = useState(false)

  useEffect(() => {
    if (!me) return
    setIsPrivate(Boolean((me.profile as any)?.is_private))
    setTipsEnabled(Boolean((me.profile as any)?.tips_enabled))
  }, [me])

  useEffect(() => {
    if (!authed) return
    setLoadingBlocked(true)
    listRelationships('block')
      .then(setBlocked)
      .catch(() => setBlocked([]))
      .finally(() => setLoadingBlocked(false))
  }, [authed])

  useEffect(() => {
    if (!authed) return
    getContentPreferences()
      .then((prefs) => {
        setAllowSensitiveNudity(Boolean(prefs.allow_sensitive_nudity))
        setAllowSensitiveViolence(Boolean(prefs.allow_sensitive_violence))
        setBlurSensitivePreviews(Boolean(prefs.blur_sensitive_previews))
        setAgeConfirmed18(Boolean(prefs.age_confirmed_18))
      })
      .catch(() => {})
  }, [authed])

  async function onSave() {
    setMsg(null)
    try {
      await updateMeProfile({ is_private: isPrivate, tips_enabled: tipsEnabled } as any)
      await updateContentPreferences({
        allow_sensitive_nudity: allowSensitiveNudity,
        allow_sensitive_violence: allowSensitiveViolence,
        blur_sensitive_previews: blurSensitivePreviews,
        age_confirmed_18: ageConfirmed18,
      })
      setMsg('Saved')
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Save failed')
    }
  }

  async function onBlockHandle() {
    const normalized = handleInput.trim().replace(/^@+/, '').toLowerCase()
    if (!normalized) return setMsg('Enter a handle to block')
    setMsg(null)
    try {
      setBlocking(true)
      await blockHandle(normalized)
      setHandleInput('')
      const rows = await listRelationships('block')
      setBlocked(rows)
      setMsg('Blocked')
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Block failed')
    } finally {
      setBlocking(false)
    }
  }

  async function onUnblock(id: number) {
    setUnblockId(id)
    setMsg(null)
    try {
      await deleteRelationship(id)
      const rows = await listRelationships('block')
      setBlocked(rows)
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || e?.message || 'Unblock failed')
    } finally {
      setUnblockId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-xl font-extrabold">Privacy &amp; Safety</h1>
        <p className="text-white/70 text-sm">Manage who can interact with you. Accounts you block cannot view your profile or contact you.</p>
      </div>

      <div className="rounded-xl border border-white/10 p-4 space-y-3">
        <label className="flex items-center justify-between">
          <div>
            <div className="font-semibold">Private profile</div>
            <div className="text-sm text-white/70">Only approved followers can view your posts.</div>
          </div>
          <input type="checkbox" checked={isPrivate} onChange={(e)=>setIsPrivate(e.target.checked)} />
        </label>
	        <label className="flex items-center justify-between">
	          <div>
            <div className="font-semibold">Enable gifts</div>
            <div className="text-sm text-white/70">Allow others to send you gifts.</div>
          </div>
          <input type="checkbox" checked={tipsEnabled} onChange={(e)=>setTipsEnabled(e.target.checked)} />
        </label>
        <label className="flex items-center justify-between">
          <div>
            <div className="font-semibold">Confirm age 18+</div>
            <div className="text-sm text-white/70">Required to reveal sensitive content.</div>
          </div>
          <input type="checkbox" checked={ageConfirmed18} onChange={(e)=>setAgeConfirmed18(e.target.checked)} />
        </label>
        <label className="flex items-center justify-between">
          <div>
            <div className="font-semibold">Allow sensitive nudity</div>
            <div className="text-sm text-white/70">Show non-explicit nudity content.</div>
          </div>
          <input type="checkbox" checked={allowSensitiveNudity} onChange={(e)=>setAllowSensitiveNudity(e.target.checked)} />
        </label>
        <label className="flex items-center justify-between">
          <div>
            <div className="font-semibold">Allow sensitive violence</div>
            <div className="text-sm text-white/70">Show graphic violence content.</div>
          </div>
          <input type="checkbox" checked={allowSensitiveViolence} onChange={(e)=>setAllowSensitiveViolence(e.target.checked)} />
        </label>
        <label className="flex items-center justify-between">
          <div>
            <div className="font-semibold">Blur sensitive previews</div>
            <div className="text-sm text-white/70">Blur content until you tap to reveal.</div>
          </div>
          <input type="checkbox" checked={blurSensitivePreviews} onChange={(e)=>setBlurSensitivePreviews(e.target.checked)} />
        </label>
        <div className="pt-2">
          <button onClick={onSave} className="px-4 py-2 rounded-lg bg-brand text-white font-semibold hover:bg-brand-600">Save</button>
        </div>
      </div>

      <section className="space-y-3">
        <h2 className="text-white/85 font-bold">Blocked accounts</h2>
        <div className="rounded-xl border border-white/10 p-4 bg-white/5">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-3">
            <input
              value={handleInput}
              onChange={(e)=>setHandleInput(e.target.value)}
              placeholder="@handle"
              className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 outline-none"
              onKeyDown={(e)=>{ if (e.key==='Enter' && !blocking) onBlockHandle() }}
            />
            <button onClick={onBlockHandle} disabled={blocking} className="px-4 py-2 rounded-lg bg-blue-500 text-white font-semibold hover:bg-blue-600 disabled:opacity-60">{blocking ? 'Blocking…' : 'Block'}</button>
          </div>
          {loadingBlocked ? (
            <div className="py-4 text-white/75">Loading…</div>
          ) : !blocked || blocked.length === 0 ? (
            <div className="text-sm text-white/70">You haven’t blocked anyone yet.</div>
          ) : (
            <ul>
              {blocked.map((rel) => (
                <li key={rel.id}>
                  <BlockedAccountRow relation={rel} onUnblock={()=>onUnblock(rel.id)} disabled={unblockId===rel.id} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {msg && <div className="rounded-lg border border-white/10 p-2 text-sm text-white/85">{msg}</div>}
    </div>
  )
}
